import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

// NOTE: Journal route still exists (/journal) — its link is temporarily hidden
// from the navigation on request. Re-add { to: "/journal", label: "Journal" }
// to restore it.
const LINKS = [
  { to: "/collection", label: "Collection" },
  { to: "/story", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [pathname]);

  const onHome = pathname === "/";
  const solid = scrolled || !onHome;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-ink/85 backdrop-blur-md border-b border-mineral/20 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <nav className="container-lux flex items-center justify-between">
        <button
          className="md:hidden text-alabaster"
          onClick={() => setMobile((m) => !m)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobile ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="hidden md:flex items-center gap-9 flex-1">
          {LINKS.slice(0, 2).map((l) => (
            <NavItem key={l.to} {...l} active={pathname === l.to} />
          ))}
        </div>

        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 md:flex-1 md:justify-center"
        >
          <img src="/brand/monogram.jpg" alt="BD" className="h-7 w-7 object-contain" />
          <span className="font-display text-xl md:text-2xl tracking-[0.18em] uppercase font-medium text-ivory hidden sm:inline">
            Beautifully&nbsp;Disturbed
          </span>
        </Link>

        <div className="flex items-center gap-6 md:gap-7 md:flex-1 md:justify-end">
          <div className="hidden md:flex items-center gap-9">
            {LINKS.slice(2).map((l) => (
              <NavItem key={l.to} {...l} active={pathname === l.to} />
            ))}
          </div>
          <Link
            to={user && user.role === "admin" ? "/admin" : "/account"}
            data-testid="account-link"
            className="text-alabaster hover:text-amethyst transition-colors duration-300"
            aria-label="Account"
          >
            <User size={18} />
          </Link>
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-toggle"
            className="relative text-alabaster hover:text-amethyst transition-colors duration-300"
            aria-label="Cart"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-amethyst text-charcoal text-[10px] font-mono w-4 h-4 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobile && (
        <div
          className="md:hidden bg-ink/95 backdrop-blur-md border-t border-mineral/20 mt-4"
          data-testid="mobile-menu"
        >
          <div className="container-lux py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="label-mono text-alabaster hover:text-amethyst"
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ to, label, active }) {
  return (
    <Link
      to={to}
      data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`label-mono transition-colors duration-300 ${
        active ? "text-amethyst" : "text-alabaster hover:text-amethyst"
      }`}
    >
      {label}
    </Link>
  );
}
